

import { useEffect, useRef, useState } from "react";

const AIChat = () => {
  const [question, setQuestion] = useState("");
  const [chatHistory, setChatHistory] = useState([]); // [{question, answer, meta?}]
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedAnswers, setExpandedAnswers] = useState({});
  const scrollRef = useRef(null);

  const suggestions = [
    "What is shellfish pollution?",
    "How can I protect myself from toxins?",
    "Is it safe to eat shellfish now?",
  ];

  // Build role-based turns for backend
  const buildTurns = () => {
    const turns = [];
    for (const item of chatHistory) {
      if (item?.question) turns.push({ role: "user", content: item.question });
      if (item?.answer) turns.push({ role: "assistant", content: item.answer });
    }
    return turns.slice(-10);
  };

  // Auto-scroll
  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const t = setTimeout(() => {
      el.scrollTop = el.scrollHeight;
      el.scrollTo?.({ top: el.scrollHeight, behavior: "smooth" });
    }, 0);
    return () => clearTimeout(t);
  }, [chatHistory, isOpen]);

  const askAI = async (customQuestion = null) => {
    const finalQuestion = (customQuestion ?? question).trim();
    if (!finalQuestion) return;
    setLoading(true);

    try {
      const res = await fetch("http://localhost:8000/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: finalQuestion,
          history: buildTurns(),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const msg = data.detail || data.error || "Request failed";
        throw new Error(msg);
      }

      const text =
        data.final_answer ??
        data.answer ?? // fallback if backend changes later
        data.detail ??
        data.error ??
        "(no answer)";

      setChatHistory((prev) => [
        ...prev,
        { question: finalQuestion, answer: text, meta: data },
      ]);
      setQuestion("");
    } catch (err) {
      const msg = err?.message || "Error: Failed to fetch response";
      setChatHistory((prev) => [
        ...prev,
        { question: finalQuestion, answer: `Error: ${msg}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (index) => {
    setExpandedAnswers((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const wordCount = (text = "") => text.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="fixed bottom-5 right-5 z-[1000]">
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 grid h-16 w-16 place-items-center rounded-full bg-blue-600 text-white text-3xl shadow-lg transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-400"
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? "✖️" : "🤖"}
      </button>

      {/* Chat Box */}
      {isOpen && (
        <div className="fixed bottom-24 right-5 flex h-[560px] w-[520px] flex-col rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h4 className="text-sm font-semibold text-slate-800">
              Shellfish Pollution Chatbot
            </h4>
          </div>

          {/* Suggestions */}
          {chatHistory.length === 0 && (
            <div className="flex flex-wrap gap-2 px-4 pb-2">
              {suggestions.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => askAI(s)}
                  className="rounded-xl bg-slate-100 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-200"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* History */}
          <div
            ref={scrollRef}
            className="mx-2 mb-3 mt-1 flex-1 overflow-y-auto rounded-lg bg-slate-50/60 p-3"
          >
            <div className="flex flex-col gap-3">
              {chatHistory.map((chat, idx) => {
                const fullAnswer = chat.answer || "";
                const shortAnswer =
                  fullAnswer.split(" ").slice(0, 35).join(" ") + "...";
                const isLong = wordCount(fullAnswer) > 35;
                const showFull = expandedAnswers[idx];

                return (
                  <div key={idx} className="flex flex-col gap-2">
                    {/* User bubble */}
                    <div className="flex w-full justify-end">
                      <div className="max-w-[80%] rounded-xl rounded-br-md bg-blue-600 px-3 py-2 text-sm text-white shadow-sm">
                        {chat.question}
                      </div>
                    </div>

                    {/* Bot bubble */}
                    <div className="flex w-full justify-start">
                      <div className="max-w-[80%] whitespace-pre-wrap rounded-xl rounded-bl-md bg-slate-100 px-3 py-2 text-sm text-slate-800 shadow-sm">
                        {isLong && !showFull ? shortAnswer : fullAnswer}
                        {isLong && (
                          <button
                            onClick={() => toggleExpand(idx)}
                            className="ml-2 inline-flex text-xs font-medium text-blue-600 hover:underline"
                          >
                            {showFull ? "less" : "more..."}
                          </button>
                        )}

                        {/* Optional details */}
                        {chat.meta?.used_queries && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-xs text-slate-600">
                              details
                            </summary>
                            <pre className="mt-1 overflow-x-auto rounded bg-white p-2 text-[11px] text-slate-700">
                              {JSON.stringify(
                                {
                                  used_queries: chat.meta.used_queries,
                                  stats: chat.meta.stats,
                                  preview_rows:
                                    chat.meta.preview_rows?.slice?.(0, 5),
                                },
                                null,
                                2
                              )}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Input Row */}
          <div className="flex items-end gap-2 border-t border-slate-200 px-3 py-3">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask a question..."
              onKeyDown={(e) => e.key === "Enter" && askAI()}
              className="min-h-[40px] w-full flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
            <button
              onClick={() => askAI()}
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? "..." : "Send"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIChat;

